// Dossier En Attente Hooks

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dossierEnAttenteAPI } from '../api/dossier-en-attente.api';
import { parseDate } from '@/core/utils/date.utils';
import type { 
  DossierEnAttenteFilters 
} from '../types/dossier-en-attente.types';

/**
 * Hook to fetch and manage dossier en attente
 */
export const useDossierEnAttente = (initialFilter?: 'urgent' | string) => {
  const [filters, setFilters] = useState<DossierEnAttenteFilters>({
    searchQuery: '',
    sortBy: 'date',
    sortOrder: 'desc',
    activeFilter: initialFilter === 'urgent' ? 'urgent' : 'all'
  });

  // Fetch dossiers
  const {
    data: expertisesData,
    isLoading: expertisesLoading,
    error: expertisesError,
    refetch: refetchExpertises
  } = useQuery({
    queryKey: ['expert-dossier-en-attente'],
    queryFn: dossierEnAttenteAPI.getDossierEnAttente,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Fetch stats
  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError
  } = useQuery({
    queryKey: ['expert-stats'],
    queryFn: dossierEnAttenteAPI.getStats,
    staleTime: 60 * 1000, // 1 minute
  });

  // Filter and sort dossiers
  const filteredAndSortedExpertises = useMemo(() => {
    if (!expertisesData?.data?.expertises) return [];

    let filtered = [...expertisesData.data.expertises];

    // Apply search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(expertise => {
        const searchableText = [
          expertise.id?.toString(),
          expertise.type,
          expertise.status,
          expertise.client?.name,
          expertise.address?.join(' '),
        ].filter(Boolean).join(' ').toLowerCase();
        
        return searchableText.includes(query);
      });
    }

    // Apply active filter
    if (filters.activeFilter === 'urgent') {
      // Filter for urgent items based on business logic
      const now = new Date();
      filtered = filtered.filter(expertise => {
        const appointmentDate = parseDate(expertise.date_du_rv);
        if (!appointmentDate) return false;
        
        const daysDiff = Math.ceil((appointmentDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        return daysDiff <= 3 && daysDiff >= 0;
      });
    } else if (filters.activeFilter === 'overdue') {
      // Filter for overdue items
      const now = new Date();
      filtered = filtered.filter(expertise => {
        const appointmentDate = parseDate(expertise.date_du_rv);
        if (!appointmentDate) return false;
        return appointmentDate < now;
      });
    } else if (filters.activeFilter === 'payment-pending') {
      // Filter for items with pending payment
      filtered = filtered.filter(expertise => {
        const status = expertise.status?.toLowerCase() || '';
        return status.includes('attente paiement') || 
               status.includes('rappel paiement') ||
               status.includes('facturation');
      });
    }

    // Apply status filter if present
    if (filters.statusFilter) {
      const statusFilterValue = filters.statusFilter;
      filtered = filtered.filter(expertise =>
        expertise.status?.toLowerCase().includes(statusFilterValue.toLowerCase())
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (filters.sortBy) {
        case 'date': {
          const dateA = parseDate(a.date_du_rv || a.date);
          const dateB = parseDate(b.date_du_rv || b.date);
          if (dateA && dateB) {
            comparison = dateA.getTime() - dateB.getTime();
          } else if (dateA) {
            comparison = -1;
          } else if (dateB) {
            comparison = 1;
          }
          break;
        }

        case 'type':
          comparison = (a.type || '').localeCompare(b.type || '');
          break;

        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '');
          break;

        case 'price': {
          const priceA = typeof a.price === 'string' ? parseFloat(a.price) : a.price || 0;
          const priceB = typeof b.price === 'string' ? parseFloat(b.price) : b.price || 0;
          comparison = priceA - priceB;
          break;
        }
      }

      return filters.sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [expertisesData, filters]);

  const updateFilter = <K extends keyof DossierEnAttenteFilters>(
    key: K,
    value: DossierEnAttenteFilters[K]
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return {
    expertises: filteredAndSortedExpertises,
    expertName: expertisesData?.data?.expert || '',
    stats: statsData?.data || null,
    isLoading: expertisesLoading || statsLoading,
    error: expertisesError || statsError,
    filters,
    updateFilter,
    refetch: refetchExpertises
  };
};